
import { isAppUsing } from '@@/bits/flip'
import { checkIfUserIsInZoomCollaborateMode } from '@@/bits/zoom'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import SurfaceActivityIndicator from '@@/vuecomponents/SurfaceActivityIndicator.vue'
import type { ResizeObserver } from 'bits/global'
import { PiniaMapState } from 'bits/pinia'
import { observeSizeChange } from 'bits/resize_observer'
import { Action, Component, defineAsyncComponent, Getter, namespace, State, Vue, Watch } from 'bits/vue'
import OzIconButton from 'library/v4/components/OzIconButton.vue'
import { debounce } from 'lodash-es'
import { useSurfaceStore } from 'pinia/surface'
import type { User } from 'types'
import AppBarAccountAvatar from 'vuecomponents/AppBarAccountAvatar.vue'
import PadletLogo, { LogoType } from 'vuecomponents/PadletLogo.vue'
import SurfaceTitleOld from 'vuecomponents/surface_title.vue'
import SurfaceHeaderMobile from 'vuecomponents/SurfaceHeaderMobile.vue'
import SurfaceNav from 'vuecomponents/SurfaceNav.vue'
import SurfaceTitle from 'vuecomponents/SurfaceTitle.vue'

@Component({
  components: {
    OzIconButton,
    PadletLogo,
    SurfaceNav,
    SurfaceTitleOld,
    AppBarAccountAvatar,
    SurfaceActivityIndicator,
    SurfaceSearchBar: defineAsyncComponent(() => import('vuecomponents/SurfaceSearchBar.vue')),
    SurfaceTitle,
    SurfaceHeaderMobile,
  },
})
export default class SurfaceHeader extends Vue {
  $el!: HTMLDivElement
  $refs!: {
    surfaceHeaderMobile: SurfaceHeaderMobile
  }

  @State isEmbedded!: boolean
  @State isElectronApp!: boolean
  @State xHeader!: boolean
  @State user!: User
  @State isUserHostingZoomCollaboration!: boolean
  @Getter dir!: 'ltr' | 'rtl'
  @Getter backgroundColor!: string
  @Getter headerColor!: string
  @Getter backgroundLuminance!: 'light' | 'dark'
  @Getter colorScheme!: 'light' | 'dark'
  @Getter isMap!: boolean
  @Getter isReadOnly!: boolean
  @Getter amIRegistered!: boolean
  @Getter homeUrl!: string
  @Getter isTitleBarTransparent!: boolean
  @Getter isDesktopLayout!: boolean
  @Getter isMobileLayout!: boolean
  @Getter canIAdminister!: boolean
  @Getter isSubmissionRequest!: boolean
  @Action resizeHeader!: (props: { height: number }) => void
  @Action checkAndSubscribeToPushNotifications!: () => void
  @namespace('settings').Action toggleSettingsPanel!: () => void
  @PiniaMapState(useSurfaceStore) searchBarTop!: number
  @PiniaMapState(useSurfaceOnboardingPanelStore) showOnboardingPanel!: boolean

  isAppUsing = isAppUsing
  resizeObserver: ResizeObserver | null = null

  get logoType(): LogoType {
    if (this.isMap) {
      return this.colorScheme === 'light' ? LogoType.Dark300 : LogoType.Light300
    }
    return this.backgroundLuminance === 'light' ? LogoType.Dark300 : LogoType.Light300
  }

  get realBackgroundLuminance(): 'light' | 'dark' {
    if (this.isMap) {
      return this.colorScheme
    }
    return this.backgroundLuminance
  }

  observeHeaderSizeChanges(): void {
    this.resizeObserver = observeSizeChange(this.$el, debounce(this.updateHeaderHeight, 250))
  }

  /**
   * Since this component doesn't have an unique root element,
   * we need to watch for the values that participate in the displaying conditions.
   * If we don't do this, we'll lose the $el reference every time the root
   * element changes, and the ResizeObserver will stop working.
   */
  @Watch('isMobileLayout')
  refreshResizeObserver(): void {
    this.$nextTick(() => {
      this.resizeObserver?.disconnect()
      this.observeHeaderSizeChanges()
    })
  }

  /**
   * ZOOM
   */
  isUserInZoomCollaborationMode = false

  get isZoomCollaborationGuest(): boolean {
    return this.isUserInZoomCollaborationMode === true && this.isUserHostingZoomCollaboration === false
  }

  async mounted(): Promise<void> {
    // Don't prompt for notifications if the user is not registered and the app is not the electron app
    if (this.amIRegistered && !this.isElectronApp) this.checkAndSubscribeToPushNotifications()

    this.updateHeaderHeight()
    this.observeHeaderSizeChanges()
    this.isUserInZoomCollaborationMode = await checkIfUserIsInZoomCollaborateMode()
  }

  beforeDestroy(): void {
    this.resizeObserver?.disconnect()
  }

  updateHeaderHeight(): void {
    if (this.isDesktopLayout) {
      this.$nextTick(() => {
        this.resizeHeader({ height: this.$el.getBoundingClientRect().height })
      })
    }
  }

  collapse(): void {
    if (!this.$refs.surfaceHeaderMobile) return
    this.$refs.surfaceHeaderMobile.collapse()
  }
}
